import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SGetKoboProjects,
  SSyncKoboProjects,
  SGetKoboAssetDetails,
  SToggleKoboAnalytics,
} from "../../apis/app.service";
import LoadingSpinner from "../sharedcomponents/LoadingSpinner";
import CreateEditKoboProject from "./components/CreateEditKoboProject/CreateEditKoboProject";
import ConfirmKoboDelete from "./components/ConfirmKoboDelete/ConfirmKoboDelete";
import ViewKoboData from "./components/ViewKoboData/ViewKoboData";
import ConfigureColumnMappings from "./components/ConfigureColumnMappings/ConfigureColumnMappings";
import { IKoboProject } from "./core/models";
import toast from "react-hot-toast";
import "./core/styles.scss";

function KoboPage() {
  const [selectedProject, setSelectedProject] = useState<IKoboProject | null>(
    null
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewDataModal, setShowViewDataModal] = useState(false);
  const [showColumnMappingsModal, setShowColumnMappingsModal] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const queryClient = useQueryClient();

  const {
    data: projects,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["koboProjects"],
    queryFn: () => SGetKoboProjects({ limit: 1000 }), // Fetch up to 1000 projects
  });

  const syncMutation = useMutation({
    mutationFn: (body: { institution?: string }) => SSyncKoboProjects(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });
      const results = data.data;
      setSyncMessage(
        `Sync completed: ${results.created} created, ${
          results.updated
        } updated, ${results.skipped} skipped${
          results.errors.length > 0 ? `, ${results.errors.length} errors` : ""
        }`
      );
      setTimeout(() => setSyncMessage(null), 5000);
    },
    onError: (error: Error) => {
      setSyncMessage(`Sync failed: ${error.message}`);
      setTimeout(() => setSyncMessage(null), 5000);
    },
  });

  const toggleAnalyticsMutation = useMutation({
    mutationFn: ({
      projectId,
      showInAnalytics,
    }: {
      projectId: string;
      showInAnalytics: boolean;
    }) => SToggleKoboAnalytics(projectId, showInAnalytics),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });

      // Update the selected project state immediately with fresh data
      if (selectedProject && data.data) {
        setSelectedProject(data.data);
      }

      toast.success(
        variables.showInAnalytics
          ? "Project enabled in analytics"
          : "Project disabled in analytics",
        {
          duration: 3000,
          position: "top-right",
        }
      );
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        duration: 3000,
        position: "top-right",
      });
    },
  });

  // Fetch asset details for the selected project
  const { data: assetDetails, isLoading: isLoadingAssetDetails } = useQuery({
    queryKey: ["koboAssetDetails", selectedProject?._id],
    queryFn: () => SGetKoboAssetDetails(selectedProject!._id),
    enabled: !!selectedProject, // Only fetch when a project is selected
  });

  // Extract available columns from asset details
  const availableColumns = useMemo(() => {
    if (!assetDetails?.data.assetDetails?.content?.survey) return [];

    const survey = assetDetails.data.assetDetails.content.survey;
    const columns: string[] = [];

    // Extract column names from survey definition.
    // Kobo returns content.survey as a FLAT array with begin_group/end_group markers,
    // not a nested structure. We track the current group path using a stack.
    const groupStack: string[] = [];
    survey.forEach((item: any) => {
      const itemType = item.type || '';
      if (itemType === 'begin_group' || itemType === 'begin group') {
        const groupName = item.name || item.$autoname;
        if (groupName) groupStack.push(groupName);
      } else if (itemType === 'end_group' || itemType === 'end group') {
        groupStack.pop();
      } else if (item.name || item.$autoname) {
        const fieldName = item.name || item.$autoname;
        const fullPath = groupStack.length > 0 ? `${groupStack.join('/')}/${fieldName}` : fieldName;
        columns.push(fullPath);
      }
    });
    return columns.sort();
  }, [assetDetails]);

  const handleSync = () => {
    // The backend will use the user's institution from the auth token
    syncMutation.mutate({});
  };

  const handleToggleAnalytics = () => {
    if (!selectedProject) return;

    toggleAnalyticsMutation.mutate({
      projectId: selectedProject._id,
      showInAnalytics: !selectedProject.showInAnalytics,
    });
  };

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    if (!projects?.docs) return [];

    let filtered = [...projects.docs];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(query) ||
          project.asset_uid.toLowerCase().includes(query) ||
          project.description?.toLowerCase().includes(query)
      );
    }

    // Sort by newest first (using createdAt or lastSynced)
    filtered.sort((a, b) => {
      const dateA = new Date(a.lastSynced || a.createdAt || 0).getTime();
      const dateB = new Date(b.lastSynced || b.createdAt || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

    return filtered;
  }, [projects, searchQuery]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="kobo-page">
        <div className="error-message">
          Error loading Kobo projects: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="kobo-page">
      <div className="kobo-container">
        <div className="kobo-header">
          <h2>KoboToolbox</h2>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={handleSync}
              disabled={syncMutation.isPending}
            >
              <i className="bx bx-sync"></i>
              {syncMutation.isPending ? "Syncing..." : "Sync from Kobo"}
            </button>
            {/* <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              Add New Project
            </button> */}
          </div>
        </div>

        {syncMessage && (
          <div
            className={`sync-message ${
              syncMessage.includes("failed") ? "error" : "success"
            }`}
          >
            {syncMessage}
          </div>
        )}

        <div className="kobo-content">
          <div className="projects-list">
            <div className="projects-list-header">
              <h3>Projects</h3>
              <div className="search-box">
                <i className="bx bx-search"></i>
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <i
                    className="bx bx-x clear-search"
                    onClick={() => setSearchQuery("")}
                  ></i>
                )}
              </div>
            </div>
            {filteredProjects.length > 0 ? (
              <>
                <p className="projects-count">
                  {filteredProjects.length} project
                  {filteredProjects.length !== 1 ? "s" : ""} found
                </p>
                <ul className="project-items">
                  {filteredProjects.map((project: IKoboProject) => (
                    <li
                      key={project._id}
                      className={`project-item ${
                        selectedProject?._id === project._id ? "active" : ""
                      }`}
                      onClick={() => setSelectedProject(project)}
                    >
                      <div className="project-info">
                        <h4>{project.name}</h4>
                        <p className="project-uid">UID: {project.asset_uid}</p>
                        {project.lastSynced && (
                          <p className="last-synced">
                            Last synced:{" "}
                            {new Date(project.lastSynced).toLocaleString()}
                          </p>
                        )}
                        {project.showInAnalytics && (
                          <span
                            className={`mapping-status ${
                              project.columnMappings?.satisfactionColumn ||
                              project.columnMappings?.waitingTimeColumn
                                ? "configured"
                                : "not-configured"
                            }`}
                          >
                            <i
                              className={`bx ${
                                project.columnMappings?.satisfactionColumn ||
                                project.columnMappings?.waitingTimeColumn
                                  ? "bx-check-circle"
                                  : "bx-error-circle"
                              }`}
                            ></i>
                            {project.columnMappings?.satisfactionColumn ||
                            project.columnMappings?.waitingTimeColumn
                              ? "Mappings Configured"
                              : "Needs Configuration"}
                          </span>
                        )}
                      </div>
                      {/* <div className="project-status">
                        <span
                          className={`status-badge ${
                            project.active ? "active" : "inactive"
                          }`}
                        >
                          {project.active ? "Active" : "Inactive"}
                        </span>
                      </div> */}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="no-projects">
                <p>
                  {searchQuery
                    ? `No projects found matching "${searchQuery}"`
                    : "No Kobo projects found."}
                </p>
                {!searchQuery && <p>Click "Add New Project" to get started.</p>}
              </div>
            )}
          </div>

          <div className="project-details">
            {selectedProject ? (
              <div className="details-content">
                <h3>{selectedProject.name}</h3>
                <div className="detail-section">
                  <h4>Project Information</h4>
                  <p>
                    <strong>Asset UID:</strong> {selectedProject.asset_uid}
                  </p>
                  {selectedProject.description && (
                    <p>
                      <strong>Description:</strong>{" "}
                      {selectedProject.description}
                    </p>
                  )}
                  {selectedProject.institution && (
                    <p>
                      <strong>Institution:</strong>{" "}
                      {selectedProject.institution.name}
                    </p>
                  )}
                </div>

                {isLoadingAssetDetails ? (
                  <div className="detail-section">
                    <p style={{ textAlign: "center", color: "#999" }}>
                      Loading details...
                    </p>
                  </div>
                ) : (
                  assetDetails?.data.assetDetails && (
                    <div className="detail-section">
                      <h4>Deployment Details</h4>
                      <p>
                        <strong>Submissions:</strong>{" "}
                        {assetDetails.data.assetDetails
                          .deployment__submission_count || 0}
                      </p>
                      <p>
                        <strong>Date Created:</strong>{" "}
                        {new Date(
                          assetDetails.data.assetDetails.date_created
                        ).toLocaleString()}
                      </p>
                      <p>
                        <strong>Date Modified:</strong>{" "}
                        {new Date(
                          assetDetails.data.assetDetails.date_modified
                        ).toLocaleString()}
                      </p>
                      {assetDetails.data.assetDetails.date_deployed && (
                        <p>
                          <strong>Date Deployed:</strong>{" "}
                          {new Date(
                            assetDetails.data.assetDetails.date_deployed
                          ).toLocaleString()}
                        </p>
                      )}
                      <p>
                        <strong>Status:</strong>{" "}
                        {assetDetails.data.assetDetails.deployment__active ? (
                          <span style={{ color: "#4caf50" }}>Active</span>
                        ) : (
                          <span style={{ color: "#f44336" }}>Inactive</span>
                        )}
                      </p>
                    </div>
                  )
                )}

                {selectedProject.showInAnalytics && (
                  <div className="detail-section">
                    <h4>Analytics Column Mappings</h4>
                    {selectedProject.columnMappings?.satisfactionColumn ||
                    selectedProject.columnMappings?.waitingTimeColumn ||
                    selectedProject.columnMappings?.locationColumn ||
                    selectedProject.columnMappings?.departmentColumn ? (
                      <>
                        {selectedProject.columnMappings?.satisfactionColumn && (
                          <p>
                            <strong>Satisfaction Rating:</strong>{" "}
                            {selectedProject.columnMappings.satisfactionColumn}
                          </p>
                        )}
                        {selectedProject.columnMappings?.waitingTimeColumn && (
                          <p>
                            <strong>Waiting Time:</strong>{" "}
                            {selectedProject.columnMappings.waitingTimeColumn}
                          </p>
                        )}
                        {selectedProject.columnMappings?.locationColumn && (
                          <p>
                            <strong>Location/Organization:</strong>{" "}
                            {selectedProject.columnMappings.locationColumn}
                          </p>
                        )}
                        {selectedProject.columnMappings?.departmentColumn && (
                          <p>
                            <strong>Department:</strong>{" "}
                            {selectedProject.columnMappings.departmentColumn}
                          </p>
                        )}
                      </>
                    ) : (
                      <p style={{ color: "#ef6c00", fontSize: "0.9rem" }}>
                        <i className="bx bx-info-circle"></i> Column mappings
                        not configured. Click "Configure Mappings" to set up the
                        data fields for analytics.
                      </p>
                    )}
                  </div>
                )}

                <div className="project-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowViewDataModal(true)}
                  >
                    View Data
                  </button>
                  {selectedProject.showInAnalytics && (
                    <button
                      className={`btn ${
                        selectedProject.columnMappings?.satisfactionColumn ||
                        selectedProject.columnMappings?.waitingTimeColumn
                          ? "btn-secondary"
                          : "btn-primary"
                      }`}
                      onClick={() => setShowColumnMappingsModal(true)}
                    >
                      <i className="bx bx-cog"></i>
                      {selectedProject.columnMappings?.satisfactionColumn ||
                      selectedProject.columnMappings?.waitingTimeColumn
                        ? "Configure Mappings"
                        : "Set Up Analytics"}
                    </button>
                  )}
                  <div className="analytics-toggle">
                    <span className="toggle-label">Show in Analytics</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={selectedProject.showInAnalytics || false}
                        onChange={handleToggleAnalytics}
                        disabled={toggleAnalyticsMutation.isPending}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  {/* <button
                    className="btn btn-secondary"
                    onClick={() => setShowEditModal(true)}
                  >
                    Edit Project
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete Project
                  </button> */}
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <p>Select a project to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateEditKoboProject
          onClose={() => {
            setShowCreateModal(false);
          }}
        />
      )}

      {showEditModal && selectedProject && (
        <CreateEditKoboProject
          project={selectedProject}
          onClose={() => {
            setShowEditModal(false);
          }}
        />
      )}

      {showDeleteModal && selectedProject && (
        <ConfirmKoboDelete
          project={selectedProject}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedProject(null);
          }}
        />
      )}

      {showViewDataModal && selectedProject && (
        <ViewKoboData
          project={selectedProject}
          onClose={() => {
            setShowViewDataModal(false);
          }}
        />
      )}

      {showColumnMappingsModal &&
        selectedProject &&
        availableColumns.length > 0 && (
          <ConfigureColumnMappings
            project={selectedProject}
            availableColumns={availableColumns}
            onClose={() => {
              setShowColumnMappingsModal(false);
            }}
            onUpdate={(updatedProject) => {
              setSelectedProject(updatedProject);
            }}
          />
        )}
    </div>
  );
}

export default KoboPage;
